<template>
  <div class="p-6" dir="rtl">
    <div class="mb-6">
      <h3 class="text-lg font-semibold mb-2">הודעה פנימית לחשיפה</h3>
      <textarea
        v-model="internalMessage"
        class="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
        :class="{
          'border-gray-300 focus:ring-blue-500': !hasUnsavedChanges,
          'border-yellow-400 bg-yellow-50 focus:ring-yellow-500': hasUnsavedChanges
        }"
        rows="4"
        placeholder="הזן הודעה פנימית..."
        @input="markAsUnsaved"
        @focusout="handleSaveMessage"
      ></textarea>
      <p v-if="messageLengthError" class="text-sm text-orange-600 mt-1">{{ messageLengthError }}</p>
      <p v-else-if="hasUnsavedChanges && !isSaving" class="text-sm text-yellow-600 mt-1">יש שינויים שלא נשמרו - לחץ מחוץ לשדה לשמירה</p>
      <p v-if="isSaving" class="text-sm text-blue-500 mt-1">שומר הודעה...</p>
    </div>

    <div class="mb-6">
      <label for="exposure-email-text" class="block text-lg font-semibold mb-2">
        טקסט שיתווסף למייל החשיפה (אופציונלי)
      </label>
      <textarea
        id="exposure-email-text"
        v-model="exposureEmailText"
        class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        rows="4"
        placeholder="הזן טקסט שיופיע במייל החשיפה..."
      ></textarea>
    </div>

    <div class="mb-4 p-3 rounded-md bg-blue-50 border border-blue-200">
      <span class="font-semibold">ברירת המחדל המוגדרת לחברה: </span>
      <span>{{ companyDefaultModeLabel }}</span>
    </div>

    <div class="mb-6">
      <h3 class="text-lg font-semibold mb-2">סוג קורות החיים שהחברה תקבל</h3>
      <div class="is-flex is-align-items-center" style="gap: 24px;">
        <label
          v-for="type in cvTypes"
          :key="type.value"
          class="is-flex is-align-items-center cv_type_option"
          :class="{ cv_type_option_selected: selectedCvType === type.value }"
          style="gap: 6px; cursor: pointer;"
        >
          <input
            type="radio"
            :value="type.value"
            v-model="selectedCvType"
            @change="onCvTypeChange"
          />
          <span>{{ type.label }}</span>
        </label>
      </div>
    </div>

    <div class="mb-6">
      <h3 class="text-lg font-semibold mb-2">בחירת קבצים</h3>
      <div class="border border-gray-200 rounded-md p-4 max-h-96 overflow-y-auto">
        <Files
          v-if="offer && offer.applicant"
          ref="filesComponent"
          :payload="{ applicant_data: offer.applicant }"
          :isSendOneFile="false"
          :defaultCvType="selectedCvType"
        />
      </div>
    </div>

    <div class="flex justify-end gap-3">
      <button
        @click="closeModal"
        class="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
      >
        ביטול
      </button>
      <button
        @click="proceed"
        class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
      >
        המשך
      </button>
    </div>
  </div>
</template>

<script>
import NetworkUtils from '@/custom/classes/network_utils.js'
import { emit } from '@/custom/classes/my_utils.js'
import { mapMutations } from 'vuex'
import Files from '@/components/Files.vue'

export default {
  name: 'OfferExposurePreparationModal',
  components: {
    Files
  },
  props: {
    payload: {
      type: Object,
      required: true
    },
    updateTitle: {
      type: Function,
      default: () => {}
    }
  },
  data () {
    return {
      internalMessage: 'חשיפה בוצעה על ידי ....... לבקשתו של ..... ',
      isSaving: false,
      hasUnsavedChanges: false,
      messageLengthError: '',
      exposureEmailText: '',
      selectedCvType: '',
      cvTypes: [
        { value: 'regular', label: 'נורמל (Normal)' },
        { value: 'hide', label: 'לייט (Lite)' },
        { value: 'super_hide', label: 'סופר לייט (Super Lite)' }
      ],
      networkUtils: new NetworkUtils()
    }
  },
  computed: {
    offer () {
      return (this.payload.props && this.payload.props.offer) || {}
    },
    companyDefaultMode () {
      return (this.offer.position &&
        this.offer.position.company &&
        this.offer.position.company.default_cv_sending_mode) || ''
    },
    companyDefaultModeLabel () {
      const labels = {
        normal: 'נורמל (Normal)',
        lite: 'לייט (Lite)',
        super_lite: 'סופר לייט (Super Lite)'
      }
      return labels[this.companyDefaultMode] || 'לא מוגדר'
    }
  },
  mounted () {
    this.updateTitle('הכנה לחשיפת הצעה')
    // Append the exposure date (like the auto-exposure message has it on the left).
    this.internalMessage = 'חשיפה בוצעה על ידי ....... לבקשתו של ..... ב-' + this.$moment().format('DD/MM/YYYY')
    // Pre-select the CV type configured as the company default (admin can change it).
    const modeToType = { normal: 'regular', lite: 'hide', super_lite: 'super_hide' }
    this.selectedCvType = modeToType[this.companyDefaultMode] || ''
  },
  methods: {
    onCvTypeChange () {
      if (this.$refs.filesComponent) {
        this.$refs.filesComponent.selectFilesByCvType(this.selectedCvType)
      }
    },

    markAsUnsaved () {
      this.hasUnsavedChanges = true
      this.messageLengthError = ''
    },

    async handleSaveMessage () {
      if (!this.hasUnsavedChanges) {
        return
      }

      if (!this.internalMessage || this.internalMessage.trim() === '') {
        this.hasUnsavedChanges = false
        this.messageLengthError = ''
        return
      }

      // Check if message is longer than 500 characters
      if (this.internalMessage.length > 500) {
        this.messageLengthError = 'ההודעה ארוכה מדי (מקסימום 500 תווים). אנא דאג לקיצור את ההודעה.'
        this.hasUnsavedChanges = true
        return
      }

      this.messageLengthError = ''
      this.isSaving = true
      try {
        const existingMessage = this.offer.message || ''
        const separator = existingMessage ? '\n\n' : ''
        const updatedMessage = existingMessage + separator + this.internalMessage

        await this.networkUtils.updateOffer({
          id: this.offer.id,
          message: updatedMessage
        })

        this.hasUnsavedChanges = false
        emit('show_toast', { text: 'הודעה פנימית נשמרה בהצלחה', type: 'success' })
      } catch (error) {
        console.error('Error saving internal message:', error)
        emit('show_toast', { text: 'שגיאה בשמירת ההודעה הפנימית', type: 'error' })
      } finally {
        this.isSaving = false
      }
    },

    async proceed () {
      const selectedFiles = this.$refs.filesComponent
        ? this.$refs.filesComponent.getCheckedFileNames() : []

      console.log('Selected files:', selectedFiles)

      // Close this modal
      this.remove(this.payload)

      // Open OfferExposureModal with selected files
      this.create({
        designator: 'offer-exposure-modal',
        component_name: 'OfferExposureModal',
        modal_title: 'חשיפת הצעה',
        props: {
          offer: this.offer,
          selectedFiles: selectedFiles,
          exposureEmailText: this.exposureEmailText.trim()
        }
      })
    },

    closeModal () {
      this.remove(this.payload)
    },

    ...mapMutations('modals', ['create', 'remove'])
  }
}
</script>

<style scoped>
textarea {
  font-family: inherit;
}
.cv_type_option {
  padding: 6px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
}
.cv_type_option_selected {
  border-color: #2563eb;
  background-color: #eff6ff;
  font-weight: 700;
}
</style>
